import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logActivity } from '../../services/adminMockData';
import { createRetailer, getAreas, adminSetSubscription, adminUpdateTokens } from '../../services/apiService';
import LocationSearchPanel from '../../components/admin/LocationSearchPanel';

// Fallback list, only used when GET /areas is unreachable. The live dropdown
// is populated from the backend so the areaId we register with is always real.
const AREAS = [
  'Amsterdam Zuid AS',
  'Winkelcentrum Amsterdamse Poort',
  'Winkelcentrum Gelderlandplein',
  'Alexandrium Rotterdam',
  'Citymall Almere',
  'Shopping Center Hoge Schie',
  'Stadshart Lelystad',
];

const AREA_CODES = {
  'Amsterdam Zuid AS': 'AZA',
  'Winkelcentrum Amsterdamse Poort': 'WAP',
  'Winkelcentrum Gelderlandplein': 'WGP',
  'Alexandrium Rotterdam': 'ALR',
  'Citymall Almere': 'CAL',
  'Shopping Center Hoge Schie': 'HSR',
  'Stadshart Lelystad': 'SHL',
};

const EMPTY = {
  shopName: '',
  email: '',
  password: '',
  password2: '',
  area: AREAS[0],
  address: '',
  postcode: '',
  city: '',
  description: '',
  instructions: '',
  lat: null,
  lng: null,
  locationVerified: false,
  locationAddress: '',
  subscription: 'tokens',
  starterTokens: 3,
};

function generatePassword() {
  return 'WI-' + Math.random().toString(36).slice(2, 8).toUpperCase();
}

// Area code for the display-only locatiecode. Areas that exist in the backend
// but not in AREA_CODES fall back to the initials of their name.
function areaCodeFor(areaName) {
  if (AREA_CODES[areaName]) return AREA_CODES[areaName];
  const initials = (areaName || '')
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 3);
  return initials || 'GEN';
}

export default function AdminNewShopPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [areas, setAreas] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // The dropdown needs the backend's areas because registration is done with
  // an areaId, not a name.
  useEffect(() => {
    let cancelled = false;
    getAreas()
      .then((rows) => {
        if (cancelled || !Array.isArray(rows) || rows.length === 0) return;
        const sorted = [...rows].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setAreas(sorted);
        setForm((f) => (
          sorted.some((a) => a.name === f.area) ? f : { ...f, area: sorted[0].name }
        ));
      })
      .catch(() => { /* keep the fallback AREAS list */ });
    return () => { cancelled = true; };
  }, []);

  const areaOptions = areas.length ? areas.map((a) => a.name) : AREAS;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.shopName.trim()) e.shopName = 'Vereist';
    if (!form.email.trim()) e.email = 'Vereist';
    if (!form.password) e.password = 'Vereist';
    // The backend rejects anything shorter than 6 characters.
    else if (form.password.length < 6) e.password = 'Minimaal 6 tekens';
    if (form.password && form.password !== form.password2) {
      e.password2 = 'Wachtwoorden komen niet overeen';
    }
    if (!form.area) e.area = 'Kies een gebied';
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    setSubmitError('');
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;

    const code = areaCodeFor(form.area);
    const locationCode = code + '-' + Math.floor(1000 + Math.random() * 9000);
    const isUnlimited = form.subscription === 'unlimited';
    const starter = parseInt(form.starterTokens, 10) || 0;
    const emailLower = form.email.toLowerCase().trim();

    // Postcode and city have no backend column, so they are folded into the
    // single `address` field to avoid losing them.
    const fullAddress = [form.address, [form.postcode, form.city].filter(Boolean).join(' ')]
      .filter((part) => part && part.trim())
      .join(', ');

    setSubmitting(true);
    let retailer;
    try {
      const areaId = areas.find((a) => a.name === form.area)?.id || null;
      const res = await createRetailer({
        name: form.shopName.trim(),
        email: emailLower,
        password: form.password,
        areaId,
        address: fullAddress,
        description: form.description,
        instructions: form.instructions,
        // The address lookup above may already have resolved the exact spot;
        // otherwise createRetailer geocodes fullAddress. Either way the shop
        // gets its own coordinates instead of its gebied's centre.
        lat: form.lat,
        lng: form.lng,
      });
      retailer = res.retailer || {};
    } catch (err) {
      setSubmitting(false);
      if (err?.status === 409) {
        setErrors({ email: 'Dit e-mailadres is al in gebruik' });
      } else {
        setSubmitError(err?.message || 'Aanmaken mislukt. Probeer het opnieuw.');
      }
      return;
    }
    // Starter tokens or Unlimited go to the database (backend 0.3), not only
    // to this browser — otherwise the retailer never sees them.
    let planWarning = '';
    if (retailer.id) {
      try {
        if (isUnlimited) {
          await adminSetSubscription(retailer.id, { plan: 'unlimited', source: 'admin' });
        } else if (starter > 0) {
          await adminUpdateTokens(retailer.id, { purchased: starter, used: 0, reason: 'Starttegoed' });
        }
      } catch (err) {
        planWarning = isUnlimited
          ? 'Account aangemaakt, maar Unlimited kon niet worden opgeslagen (backend 0.3 nodig). Zet het via Tokens.'
          : `Account aangemaakt, maar het starttegoed niet: ${err?.message || 'onbekende fout'}`;
      }
    }
    setSubmitting(false);
    if (planWarning) setSubmitError(planWarning);

    // Mirror to localStorage for the fields the backend does not store yet
    // (tokens, subscription, locatiecode). The account itself now lives in the
    // backend, so login works from any browser.
    const newShop = {
      id: retailer.id,
      email: emailLower,
      password: form.password,
      shopName: form.shopName,
      area: form.area,
      locationCode,
      address: form.address,
      postcode: form.postcode,
      city: form.city,
      description: form.description,
      instructions: form.instructions || '',
      lat: form.lat ?? null,
      lng: form.lng ?? null,
      locationVerified: !!form.locationVerified,
      locationAddress: form.locationAddress || '',
      subscription: form.subscription,
      tokensAvailable: isUnlimited ? 'unlimited' : starter,
      tokensPurchased: isUnlimited ? 'unlimited' : starter,
      tokensUsed: 0,
      status: 'actief',
      createdAt: new Date().toISOString(),
      createdBy: sessionStorage.getItem('walkin_admin_email') || sessionStorage.getItem('walkin_admin_name') || 'admin',
      lastLogin: null,
      isDemo: false,
    };

    const appendTo = (key) => {
      let arr = [];
      try { arr = JSON.parse(localStorage.getItem(key) || '[]'); } catch { arr = []; }
      arr.push(newShop);
      localStorage.setItem(key, JSON.stringify(arr));
    };
    appendTo('walkin_shops');
    appendTo('walkin_admin_shops');
    window.dispatchEvent(new Event('walkin-admin-shops-change'));

    logActivity('Nieuwe winkelier aangemaakt', {
      shop: newShop.shopName,
      email: newShop.email,
      area: newShop.area,
      locationCode,
    });

    setCreated(newShop);
  };

  const handleCopy = () => {
    if (!created) return;
    const text =
      `WalkIn inloggegevens voor ${created.shopName}:\n` +
      `E-mail: ${created.email}\n` +
      `Wachtwoord: ${created.password}\n` +
      `User ID: ${created.id}\n` +
      `Locatiecode: ${created.locationCode}\n` +
      `Gebied: ${created.area}\n` +
      `Login: https://prototype.joinwalkin.com/shop/login`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (created) {
    return (
      <div>
        <header className="admin-page-head">
          <div>
            <button className="admin-link" onClick={() => navigate('/admin/shops')}>
              ← Terug naar winkeliers
            </button>
            <h1 className="admin-page-title" style={{ marginTop: 6 }}>
              ✅ Winkelier aangemaakt!
            </h1>
          </div>
        </header>
        {submitError && (
          <div className="admin-card" style={{ background: '#FFFBEB', color: '#92400E' }}>
            {submitError}
          </div>
        )}

        <div className="admin-card" style={{ maxWidth: 540 }}>
          <div className="admin-card__title">Inloggegevens</div>
          <div className="admin-form-row">
            <div className="admin-field">
              <span className="admin-field__label">E-mail</span>
              <div className="admin-mono">{created.email}</div>
            </div>
            <div className="admin-field">
              <span className="admin-field__label">Wachtwoord</span>
              <div className="admin-mono">{created.password}</div>
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-field">
              <span className="admin-field__label">User ID</span>
              <div className="admin-mono">{created.id}</div>
            </div>
            <div className="admin-field">
              <span className="admin-field__label">Locatiecode</span>
              <div className="admin-mono">{created.locationCode}</div>
            </div>
          </div>
          <div className="admin-field">
            <span className="admin-field__label">Gebied</span>
            <div>{created.area}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <button className="admin-btn" onClick={handleCopy}>
              📋 {copied ? 'Gekopieerd!' : 'Kopieer inloggegevens'}
            </button>
            <button
              className="admin-btn admin-btn--ghost"
              onClick={() => { setForm(EMPTY); setCreated(null); setCopied(false); setErrors({}); setSubmitError(''); }}
            >
              ➕ Nog een winkelier
            </button>
            <button
              className="admin-btn admin-btn--ghost"
              onClick={() => navigate('/admin/shops')}
            >
              ← Terug naar lijst
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <header className="admin-page-head">
        <div>
          <button className="admin-link" onClick={() => navigate('/admin/shops')}>
            ← Terug naar winkeliers
          </button>
          <h1 className="admin-page-title" style={{ marginTop: 6 }}>Nieuwe winkelier</h1>
          <p className="admin-page-sub">Maak handmatig een winkelier-account aan.</p>
        </div>
      </header>

      <form className="admin-card" style={{ maxWidth: 720 }} onSubmit={handleSubmit}>
        <div className="admin-field">
          <span className="admin-field__label">Winkelnaam</span>
          <input
            className="admin-input"
            value={form.shopName}
            onChange={(e) => set('shopName', e.target.value)}
          />
          {errors.shopName && <span style={{ color: '#DC2626', fontSize: 12 }}>{errors.shopName}</span>}
        </div>

        <div className="admin-field">
          <span className="admin-field__label">E-mailadres</span>
          <input
            className="admin-input"
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
          />
          {errors.email && <span style={{ color: '#DC2626', fontSize: 12 }}>{errors.email}</span>}
        </div>

        <div className="admin-form-row">
          <div className="admin-field">
            <span className="admin-field__label">Wachtwoord</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                className="admin-input"
                style={{ flex: 1 }}
                value={form.password}
                onChange={(e) => set('password', e.target.value)}
              />
              <button
                type="button"
                className="admin-btn admin-btn--ghost admin-btn--small"
                onClick={() => {
                  const pwd = generatePassword();
                  set('password', pwd);
                  set('password2', pwd);
                }}
              >
                Genereer
              </button>
            </div>
            {errors.password && <span style={{ color: '#DC2626', fontSize: 12 }}>{errors.password}</span>}
          </div>
          <div className="admin-field">
            <span className="admin-field__label">Herhaal wachtwoord</span>
            <input
              className="admin-input"
              value={form.password2}
              onChange={(e) => set('password2', e.target.value)}
            />
            {errors.password2 && <span style={{ color: '#DC2626', fontSize: 12 }}>{errors.password2}</span>}
          </div>
        </div>

        <div className="admin-field">
          <span className="admin-field__label">Winkelgebied</span>
          <select
            className="admin-select"
            value={form.area}
            onChange={(e) => set('area', e.target.value)}
          >
            {areaOptions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        <div className="admin-form-row">
          <div className="admin-field">
            <span className="admin-field__label">Adres</span>
            <input
              className="admin-input"
              value={form.address}
              onChange={(e) => set('address', e.target.value)}
            />
          </div>
          <div className="admin-field">
            <span className="admin-field__label">Postcode</span>
            <input
              className="admin-input"
              value={form.postcode}
              onChange={(e) => set('postcode', e.target.value)}
            />
          </div>
        </div>

        <div className="admin-field">
          <span className="admin-field__label">Stad</span>
          <input
            className="admin-input"
            value={form.city}
            onChange={(e) => set('city', e.target.value)}
          />
        </div>

        <div className="admin-field">
          <span className="admin-field__label">Beschrijving</span>
          <textarea
            className="admin-input"
            rows={3}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
          />
        </div>

        <div className="admin-field">
          <span className="admin-field__label">Looproute instructies (optioneel)</span>
          <textarea
            className="admin-input"
            rows={3}
            maxLength={300}
            placeholder="bijv: Ingang Noord · Verdieping 1 · Naast de H&M"
            value={form.instructions}
            onChange={(e) => set('instructions', e.target.value.slice(0, 300))}
          />
          <span className="admin-field__hint" style={{ textAlign: 'right' }}>
            {(form.instructions || '').length}/300
          </span>
        </div>

        <LocationSearchPanel
          formData={form}
          onPatch={(patch) => setForm((f) => ({ ...f, ...patch }))}
        />

        <div className="admin-form-row">
          <div className="admin-field">
            <span className="admin-field__label">Abonnement</span>
            <select
              className="admin-select"
              value={form.subscription}
              onChange={(e) => set('subscription', e.target.value)}
            >
              <option value="tokens">Tokens</option>
              <option value="unlimited">Unlimited</option>
            </select>
          </div>
          {form.subscription === 'tokens' && (
            <div className="admin-field">
              <span className="admin-field__label">Starter tokens</span>
              <input
                className="admin-input"
                type="number"
                min={0}
                value={form.starterTokens}
                onChange={(e) => set('starterTokens', e.target.value)}
              />
            </div>
          )}
        </div>

        {submitError && (
          <div
            style={{
              background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B',
              borderRadius: 8, padding: '10px 12px', fontSize: 13, marginTop: 10,
            }}
          >
            {submitError}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
          <button
            type="button"
            className="admin-btn admin-btn--ghost"
            onClick={() => navigate('/admin/shops')}
            disabled={submitting}
          >
            Annuleren
          </button>
          <button type="submit" className="admin-btn admin-btn--orange" disabled={submitting}>
            {submitting ? 'Bezig met aanmaken…' : 'Account aanmaken'}
          </button>
        </div>
      </form>
    </div>
  );
}
